/**
 * The first map: ลั่วเจี้ยนผิง 落劍坪 and its surroundings (docs/design/STARTER_MAP.md, plan images
 * docs/review/client-v1/world/starter-map-plan.png + starter-map-hub.png). Data only; the pure queries (height,
 * water, roads) are in village/region.ts. Frame: the village's own metres (x east, z south; north is −z), so the hub
 * rows of data/layouts/village.ts sit unchanged at the centre. Rings are measured from the village edge centre (4, −6).
 *
 * The valley floor is y 0 everywhere play happens (hub, roads, meadow, west field, lake, river): the rules' water and
 * mobs stay on flat ground. Relief rises between them (rolling hills), the cliff (140 m) closes the north, the cave hill
 * shelf (15 m) and the fort's mesa (60 m) are hills, and the mountain ring beyond 1.45 km is the soft wall.
 */

export type LjP2 = readonly [number, number];

/** The region's centre (the hub edge's centre) and the playable radius (the body's soft edge). */
export const LUOJIAN_CENTRE: LjP2 = [4, -6];
export const LUOJIAN_EDGE = { x: 4, z: -6, r: 1550 } as const;
/** The terrain's half extent drawn (m; the mountain ring reaches it). */
export const LUOJIAN_HALF = 1800;
/** The hub's flat ground: a box (the village's 240 m ground plane and the market) that is always y 0. */
export const LUOJIAN_HUB_FLAT = { x0: -128, x1: 150, z0: -128, z1: 128 } as const;

/** The river's centre line (upstream from the waterfall's foot to the village stream's head; then from the stream's
 * mouth to the lake), its half width (m). The hub's own stream (VILLAGE_WATER) joins the two. */
export const LUOJIAN_RIVER_UP: readonly LjP2[] = [
  [150, -1000], [130, -820], [170, -620], [230, -470], [160, -300], [80, -160], [40, -80], [31, -48],
];
export const LUOJIAN_RIVER_DOWN: readonly LjP2[] = [[40, 34], [46, 50], [90, 120], [170, 230], [260, 320]];
/** From the lake's outflow to the south-east edge. */
export const LUOJIAN_RIVER_OUT: readonly LjP2[] = [[420, 400], [560, 470], [720, 560], [900, 700], [1120, 860], [1400, 1000], [1700, 1150]];
export const LUOJIAN_RIVER_R = 3.2;
/** The lotus lake (an ellipse). */
export const LUOJIAN_LAKE = { x: 340, z: 360, rx: 110, rz: 80 } as const;

/** The cliff's foot line (west → east): south of it the valley, north of it the highland 140 m up. */
export const LUOJIAN_CLIFF: readonly LjP2[] = [
  [-1900, -880], [-620, -900], [-420, -960], [-200, -990], [40, -1000], [150, -1010], [330, -990], [520, -930], [680, -860], [1900, -820],
];
export const LUOJIAN_CLIFF_H = 140;
/** The face's width (m from foot to rim). */
export const LUOJIAN_CLIFF_W = 26;
/** The sword cut: a notch in the rim where the waterfall drops (x on the cliff line, half width, depth from the rim). */
export const LUOJIAN_CLEFT = { x: 150, hw: 12, depth: 60, back: 90 } as const;
/** The summit: a dome on the highland, the pavilion on top (y is the ground there, m). */
export const LUOJIAN_SUMMIT = { x: -60, z: -1160, r: 150, top: 190 } as const;

/** Hills with a set shape (centre, radius, height, flat shelf: x, z, r, y). */
export interface LjHill {
  readonly x: number;
  readonly z: number;
  readonly r: number;
  readonly h: number;
  readonly shelf?: { readonly x: number; readonly z: number; readonly r: number; readonly y: number };
}
/** The south road's ledge (PLAYER-002; `roadLedge` below): its height, shelf radius, spacing and ramp length (m). */
export const LUOJIAN_ROAD_LEDGE = { y: 8, r: 13, step: 4, ramp: 40 } as const;
export const LUOJIAN_HILLS: readonly LjHill[] = [
  // The cave hill: the mouth on a 15 m shelf facing north-west.
  { x: 650, z: 710, r: 190, h: 46, shelf: { x: 585, z: 640, r: 34, y: 15 } },
  // The bandit fort's mesa (post-v1).
  { x: -1150, z: 420, r: 230, h: 60, shelf: { x: -1150, z: 420, r: 120, y: 60 } },
  // The bamboo grove's low rise (the course runs over its roofs and rocks).
  { x: 380, z: -310, r: 170, h: 10 },
  // The opening's night road (PLAYER-002): the south road climbs onto a shelf past the meadow, so the bend where the
  // strike happens sits on a ledge with a drop beside it (render3d/village/region/sets.ts dresses it).
  ...roadLedge(),
];

/**
 * The south road's ledge: flat shelves strung along the road from just past the meadow (−88, 265) to 40 m beyond the
 * bend (−10, 190), LEDGE.y up, ramps of `ramp` metres at both ends; each shelf is `r` wide round the road's middle, so
 * the ground falls off sheer (steeper than the walk limit) a few metres past the road's shoulders.
 */
function roadLedge(): LjHill[] {
  const pts: LjP2[] = [[-88, 265], [-80, 260], [-10, 190], [-0.6, 150.5]];
  const L = LUOJIAN_ROAD_LEDGE;
  const segs = pts.slice(1).map((b, i) => Math.hypot(b[0] - pts[i]![0], b[1] - pts[i]![1]));
  const total = segs.reduce((a, b) => a + b, 0);
  const out: LjHill[] = [];
  for (let s = 0; s <= total + 1e-6; s += L.step) {
    let k = 0;
    let t = s;
    while (k < segs.length - 1 && t > segs[k]!) t -= segs[k++]!;
    const a = pts[k]!;
    const b = pts[k + 1]!;
    const f = Math.min(1, t / segs[k]!);
    const x = a[0] + (b[0] - a[0]) * f;
    const z = a[1] + (b[1] - a[1]) * f;
    const u = Math.min(1, s / L.ramp, (total - s) / L.ramp);
    const y = L.y * u * u * (3 - 2 * u);
    out.push({ x, z, r: 1, h: 0, shelf: { x, z, r: L.r, y: Math.round(y * 100) / 100 } });
  }
  // Higher shelves last: each shelf pulls the ground to its height in turn, so the highest one covering a point wins.
  return out.sort((a, b) => a.shelf!.y - b.shelf!.y);
}

/** Roads and trails (centre lines; w = width in m). The hub's own paths are VILLAGE_PATHS. */
export interface LjRoad {
  readonly id: string;
  readonly pts: readonly LjP2[];
  readonly w: number;
  readonly trail?: boolean;
  /** The ground is cut and filled to the trail's own even grade (vertex heights joined straight): a bench up a slope. */
  readonly bench?: boolean;
}
export const LUOJIAN_ROADS: readonly LjRoad[] = [
  { id: 'market', pts: [[42, -27], [60, -30], [80, -31], [100, -33], [125, -38]], w: 6 },
  { id: 'south', pts: [[6, 44], [7.5, 62], [9, 110], [-10, 190], [-80, 260], [-160, 310]], w: 7 },
  { id: 'west', pts: [[-17.5, -7], [-40, -9], [-70, -12], [-180, -30], [-300, -50], [-400, -60]], w: 7 },
  { id: 'east', pts: [[125, -38], [190, -90], [250, -170], [300, -240]], w: 7 },
  // The river road: off the south road, on the river's west bank, round the lake's south shore to the cave shelf.
  { id: 'river', pts: [[8, 80], [60, 130], [150, 240], [230, 330], [300, 450], [430, 540], [540, 600], [575, 625]], w: 7 },
  // The bamboo run (the grove's own trail, past the obstacles to the chest).
  { id: 'bamboo', pts: [[250, -190], [300, -262], [345, -312], [395, -322], [445, -298], [492, -326], [520, -390]], w: 4, trail: true },
  { id: 'falls', pts: [[300, -240], [340, -360], [300, -520], [250, -700], [200, -860], [178, -950]], w: 4, trail: true },
  // The summit trail: north from the west road, up the spur in switchbacks, along the rim to the pavilion.
  {
    id: 'summit',
    pts: [[-180, -30], [-200, -200], [-250, -500], [-250, -735], [-300, -760], [-200, -800], [-300, -845], [-200, -890], [-300, -935], [-210, -985], [-150, -1060], [-60, -1150]],
    w: 4,
    trail: true,
    bench: true,
  },
  { id: 'fort', pts: [[-400, -60], [-500, -40], [-700, 80], [-900, 260], [-1080, 380]], w: 4, trail: true },
];

export type LjZoneKind = 'hub' | 'mobs' | 'run' | 'boss' | 'gather' | 'dungeon' | 'explore' | 'closed' | 'backdrop';
/** The zones (§3): centre, half sizes (an ellipse), kind, names. `flat`: the ground there is kept y 0. */
export interface LjZone {
  readonly id: string;
  readonly kind: LjZoneKind;
  readonly th: string;
  readonly zh: string;
  readonly x: number;
  readonly z: number;
  readonly rx: number;
  readonly rz: number;
  readonly flat: boolean;
}
export const LUOJIAN_ZONES: readonly LjZone[] = [
  { id: 'hub', kind: 'hub', th: 'ลั่วเจี้ยนผิง', zh: '落劍坪', x: 30, z: -10, rx: 150, rz: 130, flat: true },
  { id: 'meadow', kind: 'mobs', th: 'ทุ่งหมูป่า', zh: '野豬坡', x: -210, z: 330, rx: 150, rz: 110, flat: true },
  { id: 'bamboo', kind: 'run', th: 'ป่าไผ่ฟังลม', zh: '聽風竹林', x: 350, z: -300, rx: 170, rz: 120, flat: false },
  { id: 'westfield', kind: 'boss', th: 'ทุ่งตะวันตก', zh: '西野', x: -470, z: -60, rx: 150, rz: 115, flat: true },
  { id: 'lake', kind: 'gather', th: 'บึงบัว', zh: '蓮塘', x: 340, z: 360, rx: 140, rz: 110, flat: true },
  { id: 'cave', kind: 'dungeon', th: 'ถ้ำหมูป่า', zh: '野豬洞', x: 580, z: 630, rx: 70, rz: 55, flat: false },
  { id: 'summit', kind: 'explore', th: 'ผากระบี่ผ่า · ศาลามองกระบี่', zh: '劍劈崖 · 望劍亭', x: -60, z: -1150, rx: 220, rz: 120, flat: false },
  { id: 'fort', kind: 'closed', th: 'ค่ายผาดำ', zh: '黑石寨', x: -1150, z: 420, rx: 140, rz: 110, flat: false },
];

/** Woods (centre, radius): pine/round-tree stands, kept off zones, roads and water by the scatter. */
export const LUOJIAN_WOODS: readonly (readonly [number, number, number])[] = [
  [-700, -500, 260], [800, -300, 260], [-900, 900, 300], [200, 1000, 260], [1000, 250, 220], [-400, 700, 200],
  [-250, -600, 200], [600, -650, 180], [-650, 250, 150], [100, 600, 160],
];

export type LjPoiKind =
  | 'spawn'
  | 'arena'
  | 'watchtower'
  | 'cave'
  | 'pavilion'
  | 'waterfall'
  | 'shrine'
  | 'fortGate'
  | 'drum'
  | 'find'
  | 'stalls';
/** Points of interest (landmarks with a place in the world); y is filled from the ground by the client. */
export interface LjPoi {
  readonly id: string;
  readonly kind: LjPoiKind;
  readonly th: string;
  readonly x: number;
  readonly z: number;
  /** Facing (radians about the vertical; 0 faces +z). */
  readonly turn?: number;
  /** Radius (an arena) or height (a tower), when it has one. */
  readonly r?: number;
  readonly h?: number;
  /** Seen from afar: the far proxy is always drawn (the sightlines, §5). */
  readonly landmark?: boolean;
}
export const LUOJIAN_POIS: readonly LjPoi[] = [
  { id: 'spawn', kind: 'spawn', th: 'จุดเกิด · ประตูไผฟาง', x: 3, z: 24 },
  { id: 'drum', kind: 'drum', th: 'กลองประกาศบอสโลก', x: 7, z: -6 },
  { id: 'ape-arena', kind: 'arena', th: 'ลานลิงยักษ์ทงปี้', x: -470, z: -60, r: 30 },
  { id: 'watchtower', kind: 'watchtower', th: 'หอดูไฟ', x: -380, z: -130, h: 22, landmark: true },
  { id: 'cave-mouth', kind: 'cave', th: 'ปากถ้ำหมูป่า', x: 585, z: 640, turn: -2.4, landmark: true },
  { id: 'pavilion', kind: 'pavilion', th: 'ศาลามองกระบี่ 望劍亭', x: -60, z: -1160, landmark: true },
  { id: 'waterfall', kind: 'waterfall', th: 'น้ำตกร่องกระบี่ผ่า', x: 150, z: -1000, landmark: true },
  { id: 'shrine-south', kind: 'shrine', th: 'ศาลเจ้าริมทาง', x: -30, z: 200, turn: 0.4 },
  { id: 'shrine-cave', kind: 'shrine', th: 'ศาลเจ้าหน้าถ้ำ (จุดพัก)', x: 430, z: 530, turn: -2.2 },
  { id: 'fort-gate', kind: 'fortGate', th: 'ประตูค่ายผาดำ (ยังไม่เปิด)', x: -1080, z: 380, turn: -2.1, landmark: true },
  { id: 'summit-find', kind: 'find', th: 'ของค้นพบบนยอดผา', x: -40, z: -1150 },
  { id: 'bamboo-chest', kind: 'find', th: 'หีบปลายเส้นไผ่', x: 520, z: -390 },
  { id: 'stalls', kind: 'stalls', th: 'แผงลอยผู้เล่น', x: 113, z: -45 },
];

/** The market quarter's lots (§7; buildings x, z, width, depth, facing the lane) and their keepers' spots. */
export interface LjLot {
  readonly id: string;
  readonly th: string;
  readonly x: number;
  readonly z: number;
  readonly w: number;
  readonly d: number;
  /** Facing (radians; 0 faces +z, the lane is south of the north row). */
  readonly turn: number;
  readonly npc: LjP2;
}
export const LUOJIAN_MARKET: readonly LjLot[] = [
  { id: 'smith', th: 'ช่างตีเหล็ก', x: 55, z: -38.5, w: 9, d: 6, turn: 0, npc: [55, -31.5] },
  { id: 'herbalist', th: 'ร้านสมุนไพร', x: 68, z: -21.5, w: 8, d: 6, turn: Math.PI, npc: [68, -26] },
  { id: 'cloth', th: 'ร้านผ้า', x: 82, z: -40.5, w: 10, d: 6, turn: 0, npc: [82, -33] },
  { id: 'pages', th: 'ผู้รับซื้อหน้าตำรา', x: 96, z: -22.5, w: 9, d: 6, turn: Math.PI, npc: [96, -27] },
];

/** NPC spots in the hub (§7); the sect recruiters stand before the hall at z −25 (x from data/schools.ts). */
export const LUOJIAN_NPCS: readonly { readonly id: string; readonly th: string; readonly at: LjP2 }[] = [
  { id: 'gate-guard', th: 'ทหารยามประตู', at: [-1, 30] },
  { id: 'elder', th: 'ผู้อาวุโสหมู่บ้าน', at: [1, 6] },
  { id: 'board', th: 'กระดานประกาศ', at: [-3.5, 8.5] },
  { id: 'trainer', th: 'ครูฝึก', at: [-14, -4.5] },
  { id: 'smith', th: 'ช่างตีเหล็ก', at: [55, -31.5] },
  { id: 'herbalist', th: 'ร้านสมุนไพร', at: [68, -26] },
  { id: 'cloth', th: 'ร้านผ้า', at: [82, -33] },
  { id: 'pages', th: 'ผู้รับซื้อหน้าตำรา', at: [96, -27] },
];
export const LUOJIAN_RECRUIT_Z = -25;

/** Mob areas: the boar meadow's 4 packs of 3 (§8; ≥ 40 m apart), the elite's spot. Positions only (rules: other lane). */
export const LUOJIAN_BOAR_PACKS: readonly LjP2[] = [[-260, 300], [-190, 350], [-150, 280], [-240, 390]];
export const LUOJIAN_BOAR_ELITE: LjP2 = [-300, 360];

/**
 * Map 1 movement (the user, 2026-10-09): walk, run, ONE jump and the slide only; no qinggong courses, glides, water
 * runs or high flight here. Every place is reached on foot (roads, trails, the summit's switchback trail); the fun bits
 * are obstacles: `jump` ones (top ≤ one jump, 2.4 m apex) and `slide` ones (a log or a low gate with a gap under it).
 * Each is a solid: footprint (x, z, half length along its turn, half depth), underside y0 and top y1 over the ground.
 */
export interface LjObstacle {
  readonly id: string;
  readonly kind: 'jump' | 'slide';
  readonly look: 'log' | 'rock' | 'fence' | 'lowGate' | 'stump';
  readonly x: number;
  readonly z: number;
  readonly turn: number;
  readonly hx: number;
  readonly hz: number;
  readonly y0: number;
  readonly y1: number;
}
const ob = (id: string, kind: LjObstacle['kind'], look: LjObstacle['look'], x: number, z: number, turn: number, hx: number, hz: number, y0: number, y1: number): LjObstacle =>
  ({ id, kind, look, x, z, turn, hx, hz, y0, y1 });
export const LUOJIAN_OBSTACLES: readonly LjObstacle[] = [
  // The hub (the starting scene): a fallen log and a boulder past the sparring ground (outside the ape arena's r 12.5), the market's crates and low beam.
  ob('hub-1', 'slide', 'log', -35, 27, 0.35, 3.2, 0.42, 0.95, 1.55),
  ob('hub-2', 'jump', 'rock', -12.5, 27.5, 0.6, 1.1, 0.9, 0, 1.3),
  ob('hub-3', 'jump', 'stump', 109, -43.5, 0, 1.0, 1.0, 0, 1.2),
  ob('hub-4', 'slide', 'lowGate', 103, -50, 0, 2.2, 0.3, 1.0, 2.4),
  // The bamboo grove's run (east road → the chest): fallen logs to slide under, low fences and rocks to jump.
  ob('bb-1', 'jump', 'fence', 262, -205, 0.9, 3.5, 0.25, 0, 1.0),
  ob('bb-2', 'slide', 'log', 300, -262, 0.7, 4.5, 0.45, 0.95, 1.6),
  ob('bb-3', 'jump', 'rock', 345, -300, 0.2, 1.6, 1.2, 0, 1.5),
  ob('bb-4', 'slide', 'log', 395, -322, -0.2, 4.5, 0.45, 0.95, 1.6),
  ob('bb-5', 'jump', 'log', 445, -298, 0.4, 4, 0.5, 0, 0.9),
  ob('bb-6', 'slide', 'lowGate', 492, -326, 1.0, 2.6, 0.35, 1.0, 2.6),
  ob('bb-7', 'jump', 'stump', 512, -364, 0, 0.8, 0.8, 0, 1.2),
  // The meadow and the cave road: a couple of fallen logs across the way.
  ob('md-1', 'slide', 'log', -120, 285, 0.5, 4.5, 0.45, 0.95, 1.6),
  ob('cv-1', 'jump', 'log', 470, 565, -0.6, 3.5, 0.45, 0, 0.9),
  // The summit trail: a low gate at the trailhead, a fallen log on a switchback.
  ob('st-1', 'slide', 'lowGate', -205, -470, 0, 2.6, 0.35, 1.0, 2.6),
  ob('st-2', 'jump', 'log', -268, -820, 0.3, 3, 0.45, 0, 0.9),
];

/** The summit's spur: a ridge south of the cliff that the switchback trail climbs (the face is gentle there). */
export const LUOJIAN_SPUR = { x: -250, hw: 150, reach: 260 } as const;

/** The flying sword's ground-skim routes (a travel mount at walking height; the perf gate flies hub → cave → summit →
 * hub). The roads they follow are kept wide. */
export const LUOJIAN_FLIGHTS: readonly { readonly id: string; readonly pts: readonly LjP2[] }[] = [
  { id: 'cave', pts: [[6, 44], [8, 80], [60, 130], [150, 240], [230, 330], [300, 450], [430, 540], [575, 625]] },
  { id: 'summit', pts: [[-17, -7], [-180, -30], [-200, -200], [-250, -500], [-250, -735], [-300, -760], [-200, -800], [-300, -845], [-200, -890], [-300, -935], [-210, -985], [-150, -1060], [-60, -1150]] },
  { id: 'arena', pts: [[-20, -15], [-250, -40], [-450, -58]] },
];
